import { assign } from "lodash";
import { ChangeDetectionStrategy, Component, ElementRef, signal, ViewChild } from "@angular/core";
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, FormControl } from '@angular/forms';
import * as FlatModels from '@medsurf/flat-models';
import { AlertControlFacade, SlideAuthorFacade } from "@medsurf/flat-facades";
import { ZodSharedValidator } from "@medsurf/flat-validators";

@Component({
    selector: 'medsurf-slide-edit-media',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './media.component.html',
    styleUrls: ['./media.component.scss'],
    standalone: false
})
export class MediaComponent {
  @ViewChild('edit_overlay_template_scroll_panel', {static: false}) editPanelScrollPanel: ElementRef<HTMLDivElement>;
  
  #isSubmitted = signal(false);
  image: FlatModels.ImageEntityModels.Image | null;
  imageForm = new FormGroup(
    {
      media: new FormControl(''),
    },
    {
      validators: ZodSharedValidator.validate(
        FlatModels.ImageEntityModels.ImageSchema.pick({
          media: true,
        })
      ),
      updateOn: 'change'
    }
  );
  backgroundForm = new FormGroup(
    {
      color: new FormControl<string | null>(null),
    },
    {
      validators: ZodSharedValidator.validate(
        FlatModels.ImageEntityModels.BackgroundSchema.pick({
          color: true,
        })
      ),
      updateOn: 'change'
    }
  );
  readonly FlatModels = FlatModels;
  
  constructor(public alertControlFacade: AlertControlFacade,
              public slideAuthorFacade: SlideAuthorFacade) {
    // Update Image
    this.slideAuthorFacade.currentSelectedSlideImage$.pipe(takeUntilDestroyed()).subscribe((image) => {
      if (this.image?.id !== image?.id && this.editPanelScrollPanel) {
        this.editPanelScrollPanel.nativeElement.scrollTop = 0;
      }
      this.image = structuredClone(image);
      if (this.image) {
        this.imageForm.reset(this.image);
        if (this.image.background) {
          this.backgroundForm.reset({
            color: this.image.background.color
          });
        }
      }
      this.#isSubmitted.set(false);
    });
  }
  
  onSubmit() {
    if (this.#isSubmitted() || this.imageForm.invalid) {
      return;
    }
    this.#isSubmitted.set(true);
    
    const image = assign(this.image, this.imageForm.value);
    this.slideAuthorFacade.requestUpdateImage(image, 'history.image_update').subscribe({
      error: (result) => {
        ZodSharedValidator.handleError(result, this.alertControlFacade, this.imageForm);
        this.#isSubmitted.set(false);
      },
      next: () => {
        this.#isSubmitted.set(false);
      }
    });
  }
  
  onBackgroundSubmit() {
    if (this.#isSubmitted() || this.backgroundForm.invalid || !this.image) {
      return;
    }
    this.#isSubmitted.set(true);
    
    const image = this.image;
    if (!image.background) {
      image.background = { color: null, blendMode: FlatModels.ImageEntityModels.BlendMode.NORMAL };
    }
    image.background.color = this.backgroundForm.value.color || null;
    
    this.slideAuthorFacade.requestUpdateImage(image, 'history.image_update').subscribe({
      error: (result) => {
        ZodSharedValidator.handleError(result, this.alertControlFacade, this.backgroundForm);
        this.#isSubmitted.set(false);
      },
      next: () => {
        this.#isSubmitted.set(false);
      }
    });
  }
  
  onMediaChange(mediaId: string | null) {
    if (this.#isSubmitted()) {
      return;
    }
    this.#isSubmitted.set(true);
    
    // Get Data
    const page = structuredClone(this.slideAuthorFacade.snapshot_page());
    if (page === null) {
      throw new FlatModels.ErrorControlModels.MedsurfError(
        'notification.page_not_found',
        FlatModels.ErrorControlModels.ErrorLevel.enum.error,
        FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
        "APPS.Author.MediaComponent.onMediaChange"
      );
    }
    const image = structuredClone(this.slideAuthorFacade.snapshot_image());
    if (image === null) {
      throw new FlatModels.ErrorControlModels.MedsurfError(
        'notification.image_not_found',
        FlatModels.ErrorControlModels.ErrorLevel.enum.error,
        FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
        "APPS.Author.MediaComponent.onMediaChange"
      );
    }
    // Same image choosen
    if (mediaId === image.media) {
      return;
    }
    // Request Update Image And Thumbnail
    this.slideAuthorFacade.requestUpdateImageAndThumbnail(page, image, mediaId, 'history.image_update').subscribe({
      error: (result) => {
        ZodSharedValidator.handleError(result, this.alertControlFacade, this.imageForm);
        this.#isSubmitted.set(false);
      },
      next: () => {
        this.#isSubmitted.set(false);
      }
    });
  }
}
